import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../i18n';
import { counterpart, homePath, htmlLang, sectionPath } from '../seo/routes';
import { LanguageIcon } from './icons';

const NAV_SECTIONS = [
  { hash: 'services', labelKey: 'nav.services' },
  { hash: 'projects', labelKey: 'nav.projects' },
  { hash: 'about', labelKey: 'nav.about' },
] as const;

const navLinkClass =
  'font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors';

const LanguageToggle = () => {
  const { t } = useTranslation();
  const locale = useLocale();
  const { pathname } = useLocation();
  const nextLocale = locale === 'en' ? 'de' : 'en';

  return (
    <a
      href={counterpart(pathname, nextLocale)}
      hrefLang={htmlLang(nextLocale)}
      className="btn-ghost px-3 py-2 flex items-center gap-2"
    >
      <LanguageIcon className="h-4 w-4 text-muted-foreground" />
      <span className="sr-only">{t('nav.switchLanguage')}: </span>
      <span className="text-xs font-semibold">{nextLocale.toUpperCase()}</span>
    </a>
  );
};

const CommandHint = () => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      aria-label={`${t('nav.palette')} (⌘K)`}
      onClick={() => window.dispatchEvent(new Event('command-palette:open'))}
      className="btn-ghost hidden items-center gap-2 px-3 py-2 lg:flex"
    >
      <span className="font-mono text-xs tracking-widest text-muted-foreground" aria-hidden="true">⌘K</span>
    </button>
  );
};

export const Header = () => {
  const { t } = useTranslation();
  const locale = useLocale();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-md">
      <nav className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to={homePath(locale)} viewTransition className="flex items-center gap-3">
            <div className="w-9 h-9 clip-chamfer bg-primary flex items-center justify-center text-primary-foreground font-display font-semibold text-sm tracking-tight">
              RS
            </div>
            <span className="font-semibold hidden sm:inline">Rokas Sileikis</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {NAV_SECTIONS.map((item) => (
              <a key={item.hash} href={sectionPath(locale, item.hash)} className={navLinkClass}>
                {t(item.labelKey)}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <CommandHint />
            <a href={sectionPath(locale, 'contact')} className="btn-primary !px-3.5 !py-2 !text-xs">
              {t('nav.contact')}
            </a>
            <LanguageToggle />

            <button
              type="button"
              className="md:hidden btn-ghost px-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={t('nav.menu')}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div id="mobile-menu" className="md:hidden border-t border-border py-4">
            <div className="flex flex-col gap-4">
              {NAV_SECTIONS.map((item) => (
                <a key={item.hash} href={sectionPath(locale, item.hash)} onClick={closeMenu} className={navLinkClass}>
                  {t(item.labelKey)}
                </a>
              ))}
              <a href={sectionPath(locale, 'contact')} onClick={closeMenu} className={`${navLinkClass} !text-primary`}>
                {t('nav.contact')}
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};
